// ============================================================================
// Code Inserter
// ============================================================================

import { toPythonicIdentifier } from '@polyai/stream/utils/stringHelper';

import type { Condition, Flow } from '../../types';
import { blockIdentifier } from '../parser/block-identifier';
import {
  isCommentedLine,
  uncommentLine as regexUncommentLine,
} from '../parser/regex-parser';
import type { ConditionBlockBoundary } from '../types';

/**
 * Safely inserts condition blocks into code
 */
export class SafeCodeInserter {
  /**
   * Generates code for a condition block
   * @param {Condition} condition - The condition to generate code for
   * @param {Flow} flow - The flow containing all steps
   * @param {string} existingCode - Optional existing code to detect indentation from
   * @return {string} Generated Python code for the condition block
   */
  generateConditionBlockCode(
    condition: Condition,
    flow: Flow,
    existingCode?: string,
  ): string {
    let label: string;
    let childStepId: string | undefined;
    let isExitFlow = false;

    if (
      condition.config.$case === 'functionStepCondition' ||
      condition.config.$case === 'noCodeStepCondition' ||
      condition.config.$case === 'stepCondition'
    ) {
      label = condition.config.value.details.label;
      childStepId = condition.config.value.childStepId;
    } else if (condition.config.$case === 'exitFlowCondition') {
      label = condition.config.value.details.label;
      isExitFlow = true;
    } else {
      return '';
    }

    const variableName = toPythonicIdentifier(label);
    const indent = this.detectIndentationLevel(existingCode || '');

    let codeBlock: string;

    if (isExitFlow) {
      codeBlock = [
        `${variableName} = False`,
        `if ${variableName}:`,
        `${indent}    conv.exit_flow()`,
      ].join('\n');
    } else {
      // Get step name from childStepId
      const targetStep = childStepId ? flow.steps.entities[childStepId] : null;
      if (!targetStep) {
        return '';
      }

      const stepName = targetStep.name;
      const escapedStepName = this.escapePythonString(stepName);
      const escapedLabel = this.escapePythonString(label);

      codeBlock = [
        `${variableName} = False`,
        `if ${variableName}:`,
        `${indent}    flow.goto_step(${escapedStepName}, ${escapedLabel})`,
      ].join('\n');
    }

    // Indent the entire block
    const indentedBlock = codeBlock
      .split('\n')
      .map((line, index) => {
        if (line.trim().length === 0) {
          return line;
        }
        // First two lines (var assignment and if) get base indent
        // Third line (goto_step/exit_flow) already has indent + 4 spaces
        if (index < 2) {
          return indent + line;
        }
        return line; // Already indented correctly
      })
      .join('\n');

    return indentedBlock;
  }

  /**
   * Finds a safe insertion point for a new condition block.
   * Always appends at the end of the code to avoid inserting in the middle
   * of business logic.
   * @param {string} code - The existing Python code
   * @param {ConditionBlockBoundary[]} _existingBlocks - Unused, kept for API compat
   * @return {number} Character index where the new block should be inserted
   */
  findSafeInsertionPoint(
    code: string,
    _existingBlocks: ConditionBlockBoundary[],
  ): number {
    if (!code || code.trim().length === 0) {
      return 0;
    }

    return code.length;
  }

  /**
   * Inserts a condition block at a safe location
   * If a commented block exists with matching step name, uncomments it and updates label if needed
   * @param {string} code - The existing Python code
   * @param {Condition} condition - The condition to insert
   * @param {Flow} flow - The flow containing all steps
   * @return {string} Updated Python code with the condition block inserted
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  insertConditionBlock(code: string, condition: Condition, flow: Flow): string {
    // Check if block already exists (commented or not)
    const existingBlocks = blockIdentifier.findConditionBlocks(code);

    // Find existing block by step name (for step conditions) or variable name (for exit flow)
    const existingBlock = this.findMatchingBlock(
      existingBlocks,
      condition,
      flow,
    );

    if (existingBlock) {
      // Check if the action line (goto_step/exit_flow) is commented
      const lines = code.split('\n');
      const actionLineCommented =
        existingBlock.endLine < lines.length &&
        isCommentedLine(lines[existingBlock.endLine].trim());

      if (existingBlock.isCommented || actionLineCommented) {
        // Uncomment existing block and update label if needed
        const result: string[] = [];

        for (let i = 0; i < lines.length; i++) {
          if (i >= existingBlock.startLine && i <= existingBlock.endLine) {
            let line = regexUncommentLine(lines[i]);

            // Update label in goto_step call if it doesn't match (only for step conditions)
            if (
              i === existingBlock.endLine &&
              condition.config.$case !== 'exitFlowCondition' &&
              existingBlock.stepName
            ) {
              const expectedLabel = condition.config.value.details.label;
              const currentLabel = existingBlock.label;

              if (currentLabel !== expectedLabel) {
                // Update the label in the goto_step call
                const escapedCurrentLabel = currentLabel.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  '\\$&',
                );
                const escapedExpectedLabel =
                  this.escapePythonString(expectedLabel);

                // Pattern 1: flow.goto_step(..., "old_label") or flow.goto_step(..., 'old_label')
                line = line.replace(
                  new RegExp(`,\\s*["']${escapedCurrentLabel}["']\\s*\\)`, 'g'),
                  `, ${escapedExpectedLabel})`,
                );

                // Pattern 2: flow.goto_step(..., label="old_label") or label='old_label'
                line = line.replace(
                  new RegExp(
                    `label\\s*=\\s*["']${escapedCurrentLabel}["']`,
                    'g',
                  ),
                  `label=${escapedExpectedLabel}`,
                );
              }
            }

            result.push(line);
          } else {
            result.push(lines[i]);
          }
        }

        // Remove pass statement that was added when the action line was commented out
        return this.removePassAfterActionLine(result, existingBlock.endLine);
      }
      // Block already exists and is not commented
      return code;
    }

    // Block doesn't exist, insert new one
    const insertionPoint = this.findSafeInsertionPoint(code, existingBlocks);
    const newBlock = this.generateConditionBlockCode(condition, flow, code);

    // Ensure proper spacing with a blank line above the new block
    const beforeInsertion = code.slice(0, insertionPoint);
    const afterInsertion = code.slice(insertionPoint);

    // Build separator: ensure we end with \n\n (blank line) before the new block
    let separator = '';
    if (beforeInsertion.length > 0) {
      if (beforeInsertion.endsWith('\n\n')) {
        separator = '';
      } else if (beforeInsertion.endsWith('\n')) {
        separator = '\n';
      } else {
        separator = '\n\n';
      }
    }

    return `${beforeInsertion + separator + newBlock}\n${afterInsertion}`;
  }

  /**
   * Finds a matching block for a condition
   * For step conditions, matches by step name (not variable name, which can change)
   * For exit flow conditions, matches by variable name derived from label
   * @param {ConditionBlockBoundary[]} blocks - Array of condition block boundaries to search
   * @param {Condition} condition - The condition to find a matching block for
   * @param {Flow} flow - The flow containing all steps
   * @return {ConditionBlockBoundary | undefined} Matching condition block boundary or undefined if not found
   */
  private findMatchingBlock(
    blocks: ConditionBlockBoundary[],
    condition: Condition,
    flow: Flow,
  ): ConditionBlockBoundary | undefined {
    // Handle exit flow conditions - match by variable name
    if (condition.config.$case === 'exitFlowCondition') {
      const variableName = toPythonicIdentifier(
        condition.config.value.details.label,
      );
      return blocks.find(b => b.isExitFlow && b.variableName === variableName);
    }

    // For step conditions, match by step name (not variable name)
    // Get the step name from the condition's childStepId
    const childStepId = condition.config.value.childStepId;
    const childStep = flow.steps.entities[childStepId];

    if (!childStep) {
      // If step doesn't exist, fall back to variable name matching (for backwards compatibility)
      const variableName = toPythonicIdentifier(
        condition.config.value.details.label,
      );
      return blocks.find(b => !b.isExitFlow && b.variableName === variableName);
    }

    const conditionStepName = childStep.name;

    // Match by step name - this is the reliable identifier
    // Also ensure it's not an exit flow condition
    return blocks.find(b => !b.isExitFlow && b.stepName === conditionStepName);
  }

  /**
   * Removes a `pass` statement that immediately follows the action line
   * (inserted when the action was previously commented out).
   */
  private removePassAfterActionLine(
    lines: string[],
    actionLineIndex: number,
  ): string {
    const result = [...lines];
    // Look for `pass` right after the action line within the same block
    for (let i = actionLineIndex + 1; i < result.length; i++) {
      const trimmed = result[i].trim();
      if (trimmed === '') {
        continue;
      }
      if (trimmed === 'pass') {
        result.splice(i, 1);
      }
      break; // Only check the first non-empty line
    }
    return result.join('\n');
  }

  /**
   * Detects indentation level from code
   * @param {string} code - The Python code to detect indentation from
   * @return {string} Indentation string (typically 4 spaces)
   */
  private detectIndentationLevel(code: string): string {
    const lines = code.split('\n');

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('def ')) {
        // Find the next non-empty line to determine indentation
        const defIndex = lines.indexOf(line);
        for (let i = defIndex + 1; i < lines.length; i++) {
          const nextLine = lines[i];
          if (nextLine.trim().length > 0) {
            const indentMatch = nextLine.match(/^(\s+)/);
            if (indentMatch && indentMatch[1]) {
              return indentMatch[1];
            }
            break;
          }
        }
      }
    }

    // Default to 4 spaces (standard Python indentation)
    return '    ';
  }

  /**
   * Escapes a string for use in Python code
   * @param {string} str - The string to escape
   * @return {string} Escaped string wrapped in quotes
   */
  private escapePythonString(str: string): string {
    if (str.includes("'")) {
      return `"${str.replace(/"/g, '\\"')}"`;
    }
    return `'${str.replace(/'/g, "\\'")}'`;
  }
}

// Export singleton instance
export const codeInserter = new SafeCodeInserter();
